// WebSocket-Relay-Client für Digest Team.
//
// Ersetzt PeerJS: alle Game-Messages laufen über einen WebSocket zum
// Fokusgruppe-Server (`wss://<host>/games/digest-team/ws`). Damit ist das
// Spiel unabhängig von Browser-zu-Browser-P2P und funktioniert auch in
// WLANs, die AP-Isolation erzwingen.
//
// Siehe docs/superpowers/specs/2026-04-17-digest-team-deploy-design.md
// für Wire-Protokoll und Server-Seite.

import type { HostMsg, PeerMsg } from "./types";

function wsUrl(code: string, role: "host" | "peer"): string {
  const scheme = location.protocol === "https:" ? "wss" : "ws";
  const host = import.meta.env.DEV
    ? // In Dev gibt es keinen lokalen Fokusgruppe-Server; gegen Prod testen.
      "fokusgruppe.playful-mintful.ch"
    : location.host;
  return `${scheme}://${host}/games/digest-team/ws?code=${encodeURIComponent(code)}&role=${role}`;
}

type HostHandlers = {
  open?: (hostId: string) => void;
  peerJoin?: (peerId: string) => void;
  peerLeave?: (peerId: string) => void;
  peerMessage?: (peerId: string, msg: PeerMsg) => void;
  error?: (reason: string) => void;
  close?: () => void;
};

type PeerHandlers = {
  open?: (peerId: string) => void;
  hostMessage?: (msg: HostMsg) => void;
  hostLeft?: () => void;
  error?: (reason: string) => void;
  close?: () => void;
};

export class NetHost {
  readonly code: string;
  private _ws: WebSocket;
  private _h: HostHandlers = {};
  private _lastError: string | null = null;

  constructor(code: string) {
    this.code = code;
    this._ws = new WebSocket(wsUrl(code, "host"));
    this._ws.onmessage = (e) => this._onMessage(e.data);
    this._ws.onerror = () => {
      if (!this._lastError) this._lastError = "Verbindungsfehler";
      this._h.error?.(this._lastError);
    };
    this._ws.onclose = () => this._h.close?.();
  }

  on<K extends keyof HostHandlers>(event: K, cb: HostHandlers[K]): void {
    this._h[event] = cb;
  }

  sendTo(peerId: string, msg: HostMsg): void {
    this._safeSend({ t: "to", peerId, msg });
  }

  broadcast(msg: HostMsg): void {
    this._safeSend({ t: "all", msg });
  }

  close(): void {
    try {
      this._ws.close();
    } catch {
      /* ignore */
    }
  }

  private _safeSend(payload: unknown): void {
    if (this._ws.readyState !== WebSocket.OPEN) return;
    this._ws.send(JSON.stringify(payload));
  }

  private _onMessage(raw: string): void {
    let parsed: Record<string, unknown> | null = null;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return;
    }
    if (!parsed || typeof parsed.t !== "string") return;

    switch (parsed.t) {
      case "ready":
        this._h.open?.(String(parsed.hostId ?? this.code));
        break;
      case "peer-joined":
        this._h.peerJoin?.(String(parsed.peerId));
        break;
      case "peer-left":
        this._h.peerLeave?.(String(parsed.peerId));
        break;
      case "peer-msg":
        this._h.peerMessage?.(String(parsed.from), parsed.msg as PeerMsg);
        break;
      case "error":
        this._lastError = String(parsed.reason ?? parsed.code ?? "Unbekannter Fehler");
        this._h.error?.(this._lastError);
        break;
    }
  }
}

export class NetPeer {
  readonly code: string;
  peerId = "";
  private _ws: WebSocket;
  private _h: PeerHandlers = {};
  private _lastError: string | null = null;

  constructor(code: string) {
    this.code = code;
    this._ws = new WebSocket(wsUrl(code, "peer"));
    this._ws.onmessage = (e) => this._onMessage(e.data);
    this._ws.onerror = () => {
      if (!this._lastError) this._lastError = "Verbindungsfehler";
      this._h.error?.(this._lastError);
    };
    this._ws.onclose = () => this._h.close?.();
  }

  on<K extends keyof PeerHandlers>(event: K, cb: PeerHandlers[K]): void {
    this._h[event] = cb;
  }

  send(msg: PeerMsg): void {
    this._safeSend({ t: "msg", msg });
  }

  close(): void {
    try {
      this._ws.close();
    } catch {
      /* ignore */
    }
  }

  private _safeSend(payload: unknown): void {
    if (this._ws.readyState !== WebSocket.OPEN) return;
    this._ws.send(JSON.stringify(payload));
  }

  private _onMessage(raw: string): void {
    let parsed: Record<string, unknown> | null = null;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return;
    }
    if (!parsed || typeof parsed.t !== "string") return;

    switch (parsed.t) {
      case "ready":
        this.peerId = String(parsed.peerId ?? "");
        this._h.open?.(this.peerId);
        break;
      case "host-msg":
        this._h.hostMessage?.(parsed.msg as HostMsg);
        break;
      case "host-left":
        this._h.hostLeft?.();
        break;
      case "error":
        this._lastError = String(parsed.reason ?? parsed.code ?? "Unbekannter Fehler");
        this._h.error?.(this._lastError);
        break;
    }
  }
}
